import { logbookRoutesMESF } from "@dexteel/mesf-core";
import { configurationPath } from "../configurations";
import { calendarRoute } from "../routes/calendar.routes";
import { delayRoutes, routeDelaysManager } from "../routes/delay.routes";
import { routeMillOverview2, routeMillSchedule } from "../routes/mill.routes";
import { salesRoutes } from "../routes/sales.routes";
import { MILL_1, MILL_2, MillConfig } from "./mill-config";
import { calendarYearRoute } from "../routes/calendar-year.routes";
import {
	m1Routes,
	routeM1Overview,
	routeM1Schedule,
	routeM1ShiftReport,
} from "../routes/m1.routes";
import { routeShiftReport } from "../routes/shift_report.routes";

export type ConfigurationPath =
	| "delay-categories"
	| "delay-codes"
	| "locations"
	| "sections"
	| "section-families"
	| "operators"
	| "adjustments-types"
	| "adjustments-reasons"
	| "tensile-requirements"
	| "shift-report-config";

export interface MillExtendedConfig extends MillConfig {
	routes: Array<{ path: string; element: React.ReactNode }[]>;
	homePath: string;
	configurations: ConfigurationPath[];
}

export const MILL_1_CONFIG: MillExtendedConfig = {
	...MILL_1,
	routes: [
		delayRoutes,
		logbookRoutesMESF,
		calendarRoute,
		salesRoutes,
		calendarYearRoute,
		m1Routes,
	],
	homePath: routeM1Overview,
	configurations: [
		"locations",
		"delay-categories",
		"delay-codes",
		"operators",
		"section-families",
		"sections",
		"shift-report-config",
	],
};

export const MILL_2_CONFIG: MillExtendedConfig = {
	...MILL_2,
	routes: [],
	homePath: routeMillOverview2,
	configurations: [
		"delay-categories",
		"delay-codes",
		"locations",
		"sections",
		"section-families",
		"operators",
		"adjustments-types",
		"adjustments-reasons",
		"tensile-requirements",
		"shift-report-config",
	],
};

export const getMillConfigByAssetId = (
	assetId?: number,
): MillExtendedConfig => {
	if (assetId === MILL_1.id) {
		return MILL_1_CONFIG;
	}
	return MILL_2_CONFIG;
};

export const getHomePathByAssetId = (assetId?: number): string => {
	return getMillConfigByAssetId(assetId).homePath;
};

// Shared routes available for BOTH mills (delays, logbook, calendar, etc.)
// These should never trigger a mill-based redirect.
const SHARED_ROUTES = [
	...delayRoutes,
	...logbookRoutesMESF,
	...calendarRoute,
	...calendarYearRoute,
	...salesRoutes,
];

const isSharedRoute = (pathname: string): boolean =>
	SHARED_ROUTES.some((route) => pathname.startsWith(route.path));

export const isRouteAllowedForMill1 = (pathname: string): boolean => {
	if (isSharedRoute(pathname)) return true;

	const validRoute = MILL_1_CONFIG.routes
		.flat()
		.some((route) => pathname.startsWith(route.path));

	const validConfig = MILL_1_CONFIG.configurations.some((config) =>
		pathname.startsWith(`${configurationPath}/${config}`),
	);

	return validRoute || validConfig || pathname.startsWith("/account");
};

export const isConfigurationAllowed = (
	configPath: ConfigurationPath,
	assetId?: number,
): boolean => {
	const config = getMillConfigByAssetId(assetId);
	return config.configurations.includes(configPath);
};

/**
 * Maps equivalent pages between M1 and M2 so that switching mills
 * navigates to the same "concept" page instead of always going home.
 * Key: route prefix to match, Value: route to navigate to.
 */
const MILL_1_TO_MILL_2: [string, string][] = [
	[routeM1Overview, routeMillOverview2],
	[routeM1Schedule, routeMillSchedule],
	[routeM1ShiftReport, routeShiftReport],
];

const MILL_2_TO_MILL_1: [string, string][] = [
	["/mill/overview", routeM1Overview],
	[routeMillSchedule, routeM1Schedule],
	[routeShiftReport, routeM1ShiftReport],
];

/**
 * Given the current pathname and the target mill's assetId,
 * returns the equivalent route in the other mill — or the home path as fallback.
 */
export const getEquivalentRoute = (
	pathname: string,
	targetAssetId?: number,
): string => {
	const mappings =
		targetAssetId === MILL_1.id ? MILL_2_TO_MILL_1 : MILL_1_TO_MILL_2;

	for (const [prefix, target] of mappings) {
		if (pathname.startsWith(prefix)) {
			return target;
		}
	}

	return getHomePathByAssetId(targetAssetId);
};
